import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import { API_CONFIG, API_ENDPOINTS } from "../../constants";
import { ApiResponse } from "../../types";
import {
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
  clearAuthCookies,
} from "../../utils/cookieUtils";

/** Request body for POST `PaymentService/Payment/SearchPaymentDetails`. */
export interface SearchPaymentDetailsRequest {
  bookingid?: string;
  phone?: string;
  transactionid?: string;
  fromdate?: string;
  todate?: string;
  month?: number;
  paymentid?: number;
  username?: string;
  mobileno?: string;
  amount?: number;
  paymentmethod?: number;
  summeryofpayments?: string;
  enteredon?: string;
  hubmasterid?: number;
  pageNumber?: number;
  pageSize?: number;
  [key: string]: unknown;
}

export interface CreatePaymentLinkRequest {
  amount: number;
  currency: string;
  accept_partial: boolean;
  description: string;
  reference_id: string;
  customer: {
    name: string;
    email: string;
    contact: string;
  };
  notify: {
    sms: boolean;
    email: boolean;
  };
  reminder_enable: boolean;
  notes: Record<string, string>;
}

export interface CreateQrCodeRequest {
  type: string;
  name: string;
  usage: string;
  fixed_amount: boolean;
  payment_amount: number;
  description: string;
  orderid: string;
  notes: Record<string, string>;
}

/** POST `Booking/GetRefunds` body — matches `Booking/GetRefunds` API contract. */
export interface GetRefundsRequest {
  /** Hub filter; `0` = all hubs. */
  hubid: number;
  /** Refund status filter; `null` when not selected. */
  paymentstatus: number | null;
  /** Booking id filter; `null` when not searching by id. */
  bookingmasterid: number | null;
  username: string;
  mobilenumber: string;
  vehiclenum: string;
  /** ISO date string — ride start filter; `null` when not set. */
  ridestartdate: string | null;
  /** ISO date string — ride completion filter; `null` when not set. */
  ridecompletiondate: string | null;
  pageNumber: number;
  pageSize: number;
}

/** Normalize `GetRefunds` paged list + total. */
export function parseGetRefundsResponse(response: unknown): {
  items: unknown[];
  total: number;
} {
  const resp = response as Record<string, unknown> | null | undefined;
  if (resp == null || typeof resp !== "object") {
    return { items: [], total: 0 };
  }

  const dataField = resp.data;
  if (Array.isArray(dataField)) {
    const items = dataField as unknown[];
    return { items, total: readPagedTotalFromEnvelope(resp, items.length) };
  }

  const envelope = dataField;
  if (envelope == null || typeof envelope !== "object") {
    return { items: [], total: 0 };
  }
  const e = envelope as Record<string, unknown>;
  const itemsRaw = e.items ?? (e as { Items?: unknown[] }).Items;
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  return { items, total: readPagedTotalFromEnvelope(e, items.length) };
}

/** Read total row count from common paged API envelope fields. */
export function readPagedTotalFromEnvelope(
  envelope: Record<string, unknown> | null | undefined,
  fallback: number,
): number {
  if (envelope == null || typeof envelope !== "object") {
    return fallback;
  }
  const totalCandidate =
    envelope.totalCount ??
    envelope.TotalCount ??
    envelope.totalRecords ??
    envelope.TotalRecords ??
    envelope.total ??
    envelope.totalRows ??
    envelope.recordCount ??
    envelope.totalItemCount;
  if (typeof totalCandidate === "number" && Number.isFinite(totalCandidate)) {
    return totalCandidate;
  }
  if (totalCandidate != null && String(totalCandidate).trim() !== "") {
    const p = Number.parseInt(String(totalCandidate), 10);
    if (Number.isFinite(p)) return p;
  }
  return fallback;
}

/** Normalize paged search responses (unwrap `data`, read list + total). */
export function parseSearchPaymentDetailsResponse(response: unknown): {
  items: unknown[];
  total: number;
} {
  const resp = response as Record<string, unknown> | null | undefined;
  if (resp == null || typeof resp !== "object") {
    return { items: [], total: 0 };
  }

  const dataField = resp.data;
  /** SearchPaymentDetails: `{ success, data: Payment[], totalCount, pageNumber, ... }` — total is sibling of `data`, not inside the array. */
  if (Array.isArray(dataField)) {
    const items = dataField as unknown[];
    const total = readPagedTotalFromEnvelope(resp, items.length);
    return { items, total };
  }

  const root = dataField !== undefined ? dataField : response;
  if (root == null) {
    return { items: [], total: 0 };
  }
  if (Array.isArray(root)) {
    return { items: root, total: root.length };
  }
  if (typeof root !== "object") {
    return { items: [], total: 0 };
  }
  const r = root as Record<string, unknown>;
  const itemsRaw =
    (Array.isArray(r.payments) && r.payments) ||
    (Array.isArray(r.paymentDetails) && r.paymentDetails) ||
    (Array.isArray(r.paymentdetails) && r.paymentdetails) ||
    (Array.isArray(r.vehicles) && r.vehicles) ||
    (Array.isArray(r.Vehicles) && r.Vehicles) ||
    (Array.isArray(r.result) && r.result) ||
    (Array.isArray(r.data) && r.data) ||
    (Array.isArray(r.items) && r.items) ||
    (Array.isArray(r.Items) && r.Items) ||
    (Array.isArray(r.promoCodes) && r.promoCodes) ||
    (Array.isArray(r.PromoCodes) && r.PromoCodes) ||
    (Array.isArray(r.promocodes) && r.promocodes) ||
    [];
  const items = itemsRaw as unknown[];
  const totalCandidate =
    r.totalCount ??
    r.TotalCount ??
    r.totalRecords ??
    r.total ??
    r.totalRows ??
    r.recordCount ??
    r.totalItemCount;
  let total: number;
  if (typeof totalCandidate === "number" && Number.isFinite(totalCandidate)) {
    total = totalCandidate;
  } else if (totalCandidate != null && String(totalCandidate).trim() !== "") {
    const p = Number.parseInt(String(totalCandidate), 10);
    total = Number.isFinite(p) ? p : items.length;
  } else {
    total = items.length;
  }
  return { items, total };
}

/** GET `Booking/GetReferralSettings` — `data` object (camelCase varies by serializer). */
export interface ReferralSettingsDto {
  referralsettingid?: number;
  ReferralSettingId?: number;
  referrerreward?: number;
  ReferrerReward?: number;
  refereereward?: number;
  RefereeReward?: number;
  enablereferral?: boolean;
  EnableReferral?: boolean;
  createdby?: number;
  createdon?: string;
  modifiedby?: number;
  modifiedon?: string;
}

export interface GetReferralSettingsApiEnvelope {
  statusCode?: number;
  isSuccess?: boolean;
  success?: boolean;
  message?: string;
  data?: ReferralSettingsDto;
}

/** POST `Booking/SaveReferralSettings` body. */
export interface SaveReferralSettingsRequest {
  referrerreward: number;
  refereereward: number;
  enablereferral: boolean;
}

export interface SaveReferralSettingsApiEnvelope {
  statusCode?: number;
  isSuccess?: boolean;
  success?: boolean;
  message?: string;
  data?: unknown;
}

export interface GetPromoCodesRequest {
  codeName: string;
  /** `null` = no status filter (all). */
  status: number | null;
  /** `null` = no coupon type filter (all). */
  coupontype: string | null;
  pageNumber: number;
  pageSize: number;
}

/** Request body for POST `UserService/User/GetAllUsers`. */
export interface GetAllUsersRequest {
  name?: string | null;
  roleid?: number | null;
  hubid?: number | null;
  userstatus?: number | null;
  kycstatus?: number | null;
  registrationdate?: string | null;
  reporttype?: string;
  pageNumber?: number;
  pageSize?: number;
  [key: string]: unknown;
}

/** Same response shape as other paged list endpoints (`items`, `totalCount`, etc.). */
export function parseGetAllUsersResponse(response: unknown): {
  items: unknown[];
  total: number;
} {
  return parseSearchPaymentDetailsResponse(response);
}

/** POST GetAllSupportTicketDetails — same paged shape as other search endpoints (`items`, `totalCount`). */
export function parseSupportTicketsListResponse(response: unknown): {
  items: unknown[];
  total: number;
} {
  return parseSearchPaymentDetailsResponse(response);
}

/**
 * POST GetPromoCodes — typical envelope:
 * `{ items, totalCount, pageNumber, pageSize, totalPages }` at root or under `data`.
 */
export function parsePromoCodesListResponse(response: unknown): {
  items: unknown[];
  total: number;
} {
  const resp = response as Record<string, unknown> | null | undefined;
  if (resp == null || typeof resp !== "object") {
    return { items: [], total: 0 };
  }

  const directItems = resp.items ?? resp.Items;
  if (Array.isArray(directItems)) {
    const total = readPagedTotalFromEnvelope(
      resp as Record<string, unknown>,
      directItems.length,
    );
    return { items: directItems as unknown[], total };
  }

  const dataInner = resp.data;
  if (
    dataInner != null &&
    typeof dataInner === "object" &&
    !Array.isArray(dataInner)
  ) {
    const d = dataInner as Record<string, unknown>;
    const innerItems = d.items ?? d.Items;
    if (Array.isArray(innerItems)) {
      const total = readPagedTotalFromEnvelope(
        d as Record<string, unknown>,
        innerItems.length,
      );
      return { items: innerItems as unknown[], total };
    }
  }

  return parseSearchPaymentDetailsResponse(response);
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: { "Content-Type": "application/json" },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.api.interceptors.request.use((config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const status = error.response?.status;
        if (status !== 401) {
          return Promise.reject(this.handleError(error));
        }

        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };
        const reqUrl = String(originalRequest?.url ?? "");

        // Wrong credentials on login — stay on login page.
        if (reqUrl.includes("GetUserLoginDetails")) {
          return Promise.reject(this.handleError(error));
        }

        // Refresh itself failed — avoid retry loop, send user to login.
        if (
          reqUrl.includes("/auth/refresh") ||
          reqUrl.includes("auth/refresh")
        ) {
          this.handleLogout();
          return Promise.reject(this.handleError(error));
        }

        if (!originalRequest._retry) {
          originalRequest._retry = true;
          const refreshToken = getRefreshToken();
          if (refreshToken) {
            try {
              const newTokens = await this.refreshToken(refreshToken);
              setAuthToken(newTokens.accessToken);
              if (newTokens.refreshToken)
                setRefreshToken(newTokens.refreshToken);
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
              }
              return this.api(originalRequest);
            } catch {
              this.handleLogout();
              return Promise.reject(this.handleError(error));
            }
          }
        }

        this.handleLogout();
        return Promise.reject(this.handleError(error));
      },
    );
  }

  private async refreshToken(refreshToken: string) {
    const response = await this.api.post(API_ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    });
    return response.data;
  }

  private handleError(error: AxiosError): Error {
    if (error.response) {
      const d = error.response.data as {
        message?: unknown;
        Message?: unknown;
      } | null;
      const m = d?.message ?? d?.Message;
      if (typeof m === "string" && m.trim() !== "") return new Error(m.trim());
      return new Error("Server error");
    }
    if (error.request)
      return new Error("Network error. Please check your connection.");
    return new Error(error.message || "Something went wrong.");
  }

  private handleLogout() {
    clearAuthCookies();
    window.location.href = "/login";
  }

  private async request<T>(
    method: "get" | "post" | "put" | "patch" | "delete",
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.api.request<T>({
      url,
      method,
      data,
      ...config,
    });
    return response.data;
  }

  get<T>(url: string, config?: AxiosRequestConfig) {
    return this.request<T>("get", url, null, config);
  }

  post<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.request<T>("post", url, data, config);
  }

  put<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.request<T>("put", url, data, config);
  }

  patch<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.request<T>("patch", url, data, config);
  }

  delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.request<T>("delete", url, null, config);
  }

  async login(credentials: any) {
    try {
      const body = {
        ...credentials,
        appversion: API_ENDPOINTS.AUTH.LOGIN_APP_VERSION,
      };
      const response = await this.post<ApiResponse<any>>(
        API_ENDPOINTS.AUTH.LOGIN,
        body,
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  logout(userId: string) {
    return this.post(`${API_ENDPOINTS.AUTH.LOGOUT}?userid=${userId}`);
  }

  getProfile() {
    return this.get(API_ENDPOINTS.AUTH.PROFILE);
  }

  // Users API
  getAllUsers(payload?: Partial<GetAllUsersRequest>) {
    const rawName = payload?.name;
    const name =
      rawName == null || rawName === ""
        ? null
        : typeof rawName === "string"
          ? rawName.trim() || null
          : rawName;

    const body: GetAllUsersRequest = {
      name,
      roleid: payload?.roleid ?? null,
      hubid: payload?.hubid ?? null,
      userstatus: payload?.userstatus ?? null,
      kycstatus: payload?.kycstatus ?? null,
      registrationdate: payload?.registrationdate ?? null,
      reporttype: payload?.reporttype ?? "user",
      pageNumber: payload?.pageNumber ?? 1,
      pageSize: payload?.pageSize ?? 10,
    };
    return this.post(API_ENDPOINTS.USERS.LIST, body);
  }

  getUserById(id: string) {
    return this.get(`${API_ENDPOINTS.STAFF.GETBYID}?id=${id}`);
  }

  getUserByUserId(userid: number) {
    return this.get(`${API_ENDPOINTS.USERS.GETUSERBYUSERID}?userid=${userid}`);
  }

  getRoleHubRelationListByUserId(userid: number) {
    return this.get(
      `${API_ENDPOINTS.USERS.GET_ROLE_HUB_RELATION_LIST}?userid=${userid}`,
    );
  }

  getAllRoles() {
    return this.get(API_ENDPOINTS.USERS.GETALLROLES);
  }

  getKYCByUserId(userId: string) {
    return this.get(
      `${API_ENDPOINTS.USERS.GET_KYC_BY_USER_ID}?userId=${userId}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  getKYCByUserIdForCreateBooking(userId: number) {
    const q = new URLSearchParams({ userId: String(userId) });
    return this.get<unknown>(
      `${API_ENDPOINTS.USERS.GET_KYC_BY_USER_ID_BOOKING}?${q.toString()}`,
    );
  }

  /**
   * Filter user for create booking (query params).
   * Example: /User/FilterUser?customername=...&phonenumber=...&email=...
   */
  filterUserForBooking(params: {
    customername?: string;
    phonenumber?: string;
    email?: string;
  }) {
    const sp = new URLSearchParams();
    const cn = params.customername?.trim();
    const pn = params.phonenumber?.trim();
    const em = params.email?.trim();
    if (cn) sp.set("customername", cn);
    if (pn) sp.set("phonenumber", pn);
    if (em) sp.set("email", em);
    const qs = sp.toString();
    const url = `${API_ENDPOINTS.USERS.FILTER_USER}${qs ? `?${qs}` : ""}`;
    return this.get(url, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  createOrUpdateUser(data: any) {
    const formData = new FormData();

    const appendFormData = (formData: FormData, data: any, parentKey = "") => {
      Object.keys(data).forEach((key) => {
        const value = data[key];
        if (value === null || value === undefined) return;

        const formKey = parentKey ? `${parentKey}.${key}` : key;

        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(
              formKey,
              typeof item === "object" ? JSON.stringify(item) : String(item),
            );
          });
        } else if (typeof value === "object") {
          appendFormData(formData, value, formKey);
        } else {
          formData.append(formKey, value);
        }
      });
    };

    appendFormData(formData, data);

    return this.api
      .post(API_ENDPOINTS.STAFF.CREATE, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => response.data); // Return response.data directly for consistency
  }

  getHubs(params?: any) {
    return this.get(API_ENDPOINTS.HUBS.LIST, { params });
  }

  getAllHubs() {
    return this.get(API_ENDPOINTS.HUBS.LIST, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getHubById(id: string) {
    return this.get(`${API_ENDPOINTS.HUBS.GETBYID}?id=${id}`, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updateHub(id: string, data: any) {
    return this.api.post(`${API_ENDPOINTS.HUBS.UPDATE}`, data, {
      baseURL: API_CONFIG.BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  deleteHub(id: string) {
    return this.get(API_ENDPOINTS.HUBS.DELETE, {
      params: { id },
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  createHub(data: any) {
    return this.api.post(API_ENDPOINTS.HUBS.CREATE, data, {
      baseURL: API_CONFIG.BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  getHubStats() {
    return this.get(API_ENDPOINTS.HUBS.STATS, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getAllVehicles(payload: {
    pageNumber: number;
    pageSize: number;
    vehiclenum: string;
    registrationnumber: string;
    telematicsid: string;
    vehiclemodel: number;
    hubmasterid: number;
    status: number;
  }) {
    return this.post(API_ENDPOINTS.VEHICLES.LIST, payload, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  /** POST `Hub_Vehicle_Staff/VehicleManagement/GetVehiclesByHubId` — body `{ hubmasterid, pageNumber, pageSize }`. */
  getVehiclesByHubId(payload: {
    hubmasterid: number;
    pageNumber?: number;
    pageSize?: number;
  }) {
    const body = {
      hubmasterid: Number(payload.hubmasterid),
      pageNumber: payload.pageNumber ?? 1,
      pageSize: payload.pageSize ?? 500,
    };
    return this.post(API_ENDPOINTS.VEHICLES.GETBYHUBID, body, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updateVehicleStatus(id: number, status: number) {
    return this.get(API_ENDPOINTS.VEHICLES.UPDATE_STATUS(id, status), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getVehicleById(id: string) {
    return this.get(`${API_ENDPOINTS.VEHICLES.GETBYID}?id=${id}`, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  createVehicle(data: any) {
    return this.post(API_ENDPOINTS.VEHICLES.CREATE, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updateVehicle(id: string, data: any) {
    return this.post(API_ENDPOINTS.VEHICLES.UPDATE(id), data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  deleteVehicle(id: string) {
    return this.get(`${API_ENDPOINTS.VEHICLES.DELETE(id)}?id=${id}`, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  uploadVehicleBulk(data: FormData, userId: number) {
    return this.api.post(
      `/Hub_Vehicle_Staff/VehicleManagement/ReadVehiclesFromExcel?userid=${userId}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 1200000, // 20 minutes timeout for bulk upload operations
      },
    );
  }

  getStaff(params?: any) {
    return this.get(API_ENDPOINTS.STAFF.LIST, { params });
  }

  getAllStaff() {
    return this.get(API_ENDPOINTS.STAFF.LIST);
  }

  createStaff(data: any) {
    return this.post(API_ENDPOINTS.STAFF.CREATE, data);
  }

  deleteStaff(id: string) {
    return this.get(`${API_ENDPOINTS.STAFF.DELETE}?id=${id}`);
  }

  getStaffStats() {
    return this.get(API_ENDPOINTS.STAFF.STATS);
  }

  getVehicleStats() {
    return this.get(API_ENDPOINTS.VEHICLES.STATS, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getAllPlans() {
    return this.get(API_ENDPOINTS.PLANS.LIST, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getPlanById(id: string) {
    return this.get(`${API_ENDPOINTS.PLANS.GETBYID}?id=${id}`, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getPlansByHubId(hubId: string) {
    return this.get(`${API_ENDPOINTS.PLANS.GETBYHUBID}?hubId=${hubId}`, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  createPlan(data: any) {
    return this.post(API_ENDPOINTS.PLANS.CREATE, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updatePlan(id: string, data: any) {
    return this.post(API_ENDPOINTS.PLANS.UPDATE, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  deletePlan(id: string) {
    return this.get(API_ENDPOINTS.PLANS.DELETE(id), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getPlanStats() {
    return this.get(API_ENDPOINTS.PLANS.STATS, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getPayments(params?: any) {
    return this.get(API_ENDPOINTS.PAYMENTS.LIST, { params });
  }

  getAllPayments() {
    return this.get(API_ENDPOINTS.PAYMENTS.LIST);
  }

  createPayment(data: any) {
    return this.post(API_ENDPOINTS.PAYMENTS.CREATE, data);
  }

  createPaymentLink(data: CreatePaymentLinkRequest) {
    return this.post(API_ENDPOINTS.PAYMENTS.CREATE_PAYMENT_LINK, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  createQrCode(data: CreateQrCodeRequest) {
    return this.post(API_ENDPOINTS.PAYMENTS.CREATE_QR_CODE, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  verifyPaymentByOrderId(
    orderId: string,
    paymenttype: string,
    qrId: string | null,
  ) {
    const q =
      qrId != null && String(qrId).trim() !== "" ? String(qrId).trim() : "";
    return this.get(
      `${API_ENDPOINTS.PAYMENTS.VERIFY_PAYMENT_BY_ORDER_ID}?orderId=${encodeURIComponent(orderId)}&paymenttype=${encodeURIComponent(paymenttype)}&qrId=${encodeURIComponent(q)}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  updatePayment(id: string, data: any) {
    return this.put(API_ENDPOINTS.PAYMENTS.UPDATE(id), data);
  }

  deletePayment(id: string) {
    return this.delete(API_ENDPOINTS.PAYMENTS.DELETE(id));
  }

  getPaymentStats() {
    return this.get(API_ENDPOINTS.PAYMENTS.STATS);
  }

  /**
   * POST SearchPaymentDetails. Use `pageNumber` ≥ 1 and `pageSize` &gt; 0 for paged results.
   */
  searchPaymentDetails(data: SearchPaymentDetailsRequest) {
    return this.post(API_ENDPOINTS.PAYMENTS.SEARCH, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getPaymentDetails(paymentId: string) {
    return this.get(
      `${API_ENDPOINTS.PAYMENTS.GET_DETAILS}?paymentId=${paymentId}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  /** Record payment when editing booking (plan step save). */
  savePaymentDetails(data: {
    paymentid: number;
    hubmasterid: number;
    bookingid: string;
    bookingplanid: number;
    username: string;
    mobileno: string;
    hubname: string;
    amount: number;
    paymentmethod: number;
    summeryofpayments: string;
    transactionid: string;
    enteredon: string;
    discountedamount?: number;
    orderid: string;
  }) {
    const isCashPayment =
      String(data.transactionid ?? "")
        .trim()
        .toLowerCase() === "cash";
    return this.post(
      API_ENDPOINTS.PAYMENTS.SAVE_DETAILS,
      {
        ...data,
        ispaymentpending: !isCashPayment,
      },
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  createRazorpayOrder(payload: {
    amount: number;
    currency: string;
    receipt: string;
    notes?: Record<string, string>;
    extraobject?: string[];
  }) {
    const extraobject =
      Array.isArray(payload.extraobject) && payload.extraobject.length > 0
        ? payload.extraobject.map((s) => String(s))
        : [String(payload.receipt)];
    const body: Record<string, unknown> = {
      amount: payload.amount,
      currency: payload.currency,
      receipt: payload.receipt,
      extraobject,
    };
    if (payload.notes != null && Object.keys(payload.notes).length > 0) {
      body.notes = payload.notes;
    }
    return this.post<{
      success?: boolean;
      data?: { id: string; amount: number; currency?: string; status?: string };
    }>(API_ENDPOINTS.PAYMENTS.CREATE_RAZORPAY_ORDER, body, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getBookings(payload: {
    fromDate: string | null;
    toDate: string | null;
    searchText: string;
    rideStatus: number | null;
    pageNumber: number;
    pageSize: number;
    hubmasterid?: number;
  }) {
    return this.post(API_ENDPOINTS.BOOKINGS.LIST, payload, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getBookingById(bookingId: string) {
    return this.post(API_ENDPOINTS.BOOKINGS.DETAILS(bookingId), null, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  deleteBookingByBookingId(bookingId: string) {
    return this.post(
      API_ENDPOINTS.BOOKINGS.DELETE_BY_BOOKING_ID(bookingId),
      null,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  cancelBooking(bookingId: string, cancelReason: string) {
    return this.post(
      API_ENDPOINTS.BOOKINGS.CANCEL_BOOKING(bookingId, cancelReason),
      null,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  abscondUser(bookingmasterid: string | number) {
    return this.post(API_ENDPOINTS.BOOKINGS.ABSCOND_USER(bookingmasterid), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  savePlanExpiryDateExtension(data: {
    bookingmasterid: number;
    previousenddate: string;
    extendeddate: string;
    extensionreason: string;
  }) {
    return this.post(
      API_ENDPOINTS.BOOKINGS.SAVE_PLAN_EXPIRY_DATE_EXTENSION,
      data,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  getPlanExpiryDateExtensions(bookingmasterid: string | number) {
    return this.get(
      API_ENDPOINTS.BOOKINGS.GET_PLAN_EXPIRY_DATE_EXTENSIONS(bookingmasterid),
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  /** Audit / activity log for a booking (GetAuditLogsByBookingId) */
  getAuditLogsByBookingId(bookingId: string | number) {
    return this.get(
      API_ENDPOINTS.BOOKINGS.GET_AUDIT_LOGS_BY_BOOKING_ID(bookingId),
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  getBookingsByUserId(userId: string) {
    return this.post(API_ENDPOINTS.BOOKINGS.GET_BY_USER_ID(userId), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  createBooking(data: unknown) {
    return this.post(API_ENDPOINTS.BOOKINGS.CREATE, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updateBooking(id: string, data: any) {
    return this.put(API_ENDPOINTS.BOOKINGS.UPDATE(id), data);
  }

  updateBookingPayload(data: any) {
    return this.post(API_ENDPOINTS.BOOKINGS.UPDATE_BOOKING, data);
  }

  getCostBreakdownDifference(params: {
    oldplanid: number;
    newplanid: number;
    bookingmasterid: number;
    oldbookingplanid: number;
    /** When true, same plan ids as current booking (vehicle-only change). Default false when a new plan is selected. */
    onlyvehiclechange?: boolean;
  }) {
    const url = API_ENDPOINTS.BOOKINGS.GET_COST_BREAKDOWN_DIFFERENCE(params);
    return this.post(url, {});
  }

  deleteBooking(id: string) {
    return this.delete(API_ENDPOINTS.BOOKINGS.DELETE(id));
  }

  getBookingStats() {
    return this.get(API_ENDPOINTS.BOOKINGS.STATS);
  }

  exportBookingDetails(payload: {
    fromDate: string | null;
    toDate: string | null;
    searchText: string;
    rideStatus: string | null;
  }) {
    return this.post(API_ENDPOINTS.BOOKINGS.EXPORT_BOOKING_DETAILS, payload, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getRideManagementData<T = unknown>(
    payload: {
      bookingid?: string | null;
      duedate?: string | null;
      pageNumber?: number;
      pageSize?: number;
      hubmasterid?: number;
    } = {},
  ) {
    const requestPayload = {
      bookingid: payload.bookingid ?? null,
      duedate: payload.duedate ?? null,
      pageNumber: payload.pageNumber ?? 1,
      pageSize: payload.pageSize ?? 10,
      hubmasterid:
        typeof payload.hubmasterid === "number" &&
        Number.isFinite(payload.hubmasterid)
          ? payload.hubmasterid
          : 0,
    };

    return this.post<T>(API_ENDPOINTS.RIDES.LIST, requestPayload, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getRideDetails<T = any>(bookingId: string) {
    return this.post<T>(API_ENDPOINTS.RIDES.DETAILS(bookingId), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getTripsByUserId<T = unknown>(userId: string | number) {
    return this.post<T>(API_ENDPOINTS.RIDES.GET_TRIP_BY_USER_ID(userId), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getTelematicsDeviceData(bookingmasterid: string | number) {
    return this.post(
      `${API_ENDPOINTS.RIDES.TELEMATICS}?bookingmasterid=${bookingmasterid}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  getVehicleConditionCheckList(checklistType: number) {
    return this.post(
      `${API_ENDPOINTS.RIDES.GET_VEHICLE_CONDITION_CHECKLIST}?checklistType=${checklistType}`,
      {},
      { baseURL: API_CONFIG.BASE_URL },
    );
  }

  searchSupportTickets(payload: {
    userId: number;
    pageNumber?: number;
    pageSize?: number;
    ticketid: string;
    createdDate: string | null;
    assignee: number;
    ticketpriorityid: number | null;
    statusid: number;
    ticketcategoryid: number | null;
  }) {
    const body = {
      ...payload,
      pageNumber: payload.pageNumber ?? 1,
      pageSize: payload.pageSize ?? 10,
    };
    return this.post(API_ENDPOINTS.SUPPORT.LIST, body, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getSupportTicketDetails(ticketId: string) {
    return this.get(API_ENDPOINTS.SUPPORT.DETAILS(ticketId), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updateUserStatus(userstatus: number, userid: string | number) {
    return this.post(
      `${API_ENDPOINTS.USERS.UPDATE_STATUS}?userstatus=${userstatus}&userid=${userid}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  saveSupportTicketNotes(data: {
    supportticketdetailid: number;
    supportticketid: number;
    assignee: number;
    statusid: number;
    note: string;
    medias: string[];
    userid: number;
    enteredon: string;
    ticketpriorityid?: number;
  }) {
    return this.post(API_ENDPOINTS.SUPPORT.SAVE_NOTES, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  saveSupportTicketDetails(data: {
    ticketmasterid: number;
    ticketid: string;
    userid: number;
    enteredon: string;
    username: string;
    phoneno: string;
    ticketcategoryid: number;
    ticketpriorityid: number;
    statusid: number;
    hubmasterid: number;
    hubname: string;
    assignee: number;
    description: string;
    medias: string[];
    bookingid: number;
  }) {
    return this.post(API_ENDPOINTS.SUPPORT.SAVE_TICKET_DETAILS, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updateSupportTicketDetails(data: {
    ticketmasterid: number;
    ticketid: string;
    userid: number;
    enteredon: string;
    username: string;
    phoneno: string;
    ticketcategoryid: number;
    ticketpriorityid: number;
    statusid: number;
    hubmasterid: number;
    hubname: string;
    assignee: number;
    description: string;
    medias: string[];
    bookingid: number;
  }) {
    return this.post(API_ENDPOINTS.SUPPORT.UPDATE_TICKET_DETAILS, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getDashboardStats() {
    return this.get(API_ENDPOINTS.DASHBOARD.STATS);
  }

  getDashboardData(payload: {
    hubid: number;
    filterDashboardDataReqBO: {
      trendtype_filtertype: {
        revenue: string;
        ride: string;
        customer: string;
      };
    };
  }) {
    return this.post(API_ENDPOINTS.DASHBOARD.GET_DASHBOARD_DATA, payload, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  uploadImage(
    file: File,
    folderPath: string = "staff",
    folderType: string = "profile",
  ) {
    const formData = new FormData();

    formData.append("Images", file);

    formData.append("FolderPath", folderPath);
    formData.append("FolderType", folderType);

    return this.api.post(API_ENDPOINTS.COMMON.SAVE_IMAGE, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  getStates() {
    return this.get(API_ENDPOINTS.COMMON.GET_STATES);
  }

  getCitiesByState(stateId: number) {
    return this.get(
      `${API_ENDPOINTS.COMMON.GET_CITIES_BY_STATE}?stateId=${stateId}`,
    );
  }

  getCities() {
    return this.get(API_ENDPOINTS.COMMON.GET_CITIES, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getAllCodeCommon() {
    return this.get(API_ENDPOINTS.COMMON.GET_ALL_CODE_COMMON);
  }

  getAllCodeCommonByCategory(categoryNames: string) {
    return this.get(
      `${API_ENDPOINTS.COMMON.GET_ALL_CODE_COMMON_BY_CATEGORY}?categoryNames=${categoryNames}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  getAllTemplates() {
    return this.get(API_ENDPOINTS.TEMPLATES.LIST, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getTemplateByIdOrName(templateMasterId: string) {
    return this.post(
      `${API_ENDPOINTS.TEMPLATES.GETBYIDORNAME}?templateMasterId=${templateMasterId}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  createTemplate(data: any) {
    return this.post(API_ENDPOINTS.TEMPLATES.CREATE, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updateTemplate(data: any) {
    return this.post(API_ENDPOINTS.TEMPLATES.UPDATE, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  deleteTemplate(templateMasterId: string) {
    return this.post(
      `${API_ENDPOINTS.TEMPLATES.DELETE}?templateMasterId=${templateMasterId}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  saveTermsPrivacyAndAgreements(
    data: Array<{
      agreementname: string;
      agreementpath: string;
      documenttype: number;
    }>,
  ) {
    return this.post(
      API_ENDPOINTS.SETTINGS.SAVE_TERMS_PRIVACY_AND_AGREEMENTS,
      data,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  getPrivacyTermsTemplate(documenttype: number) {
    return this.get(
      `${API_ENDPOINTS.SETTINGS.GET_PRIVACY_TERMS_TEMPLATE}?documenttype=${documenttype}`,
      {
        baseURL: API_CONFIG.BASE_URL,
      },
    );
  }

  getSystemSettingsData() {
    return this.get(API_ENDPOINTS.SETTINGS.GET_SYSTEM_SETTINGS_DATA);
  }

  /** Promo codes list — POST `GetPromoCodes` (paged search). */
  searchPromoCodes(params: Partial<GetPromoCodesRequest> = {}) {
    const body: GetPromoCodesRequest = {
      codeName:
        typeof params.codeName === "string" ? params.codeName.trim() : "",
      status:
        params.status === null
          ? null
          : typeof params.status === "number" && Number.isFinite(params.status)
            ? params.status
            : null,
      coupontype:
        typeof params.coupontype === "string" &&
        params.coupontype.trim().length > 0
          ? params.coupontype.trim()
          : null,
      pageNumber:
        typeof params.pageNumber === "number" &&
        Number.isFinite(params.pageNumber) &&
        params.pageNumber >= 1
          ? params.pageNumber
          : 1,
      pageSize:
        typeof params.pageSize === "number" &&
        Number.isFinite(params.pageSize) &&
        params.pageSize > 0
          ? params.pageSize
          : 10,
    };
    return this.post(API_ENDPOINTS.PROMO_CODES.LIST, body, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getPromoCodesByUserId(userId: number) {
    return this.get(API_ENDPOINTS.PROMO_CODES.BY_USER_ID, {
      baseURL: API_CONFIG.BASE_URL,
      params: { userId },
    });
  }

  /** POST `CheckCouponValidity` — validate coupon against plan price. */
  checkCouponValidity(data: {
    couponid: number;
    couponName: string;
    isReferralCoupon: boolean;
    planprice: number;
    userid: number;
  }) {
    return this.post(API_ENDPOINTS.PROMO_CODES.CHECK_VALIDITY, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  savePromoCode(data: any) {
    return this.post(API_ENDPOINTS.PROMO_CODES.CREATE, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getPromoCodeById(couponId: string) {
    return this.get(API_ENDPOINTS.PROMO_CODES.GET_BY_ID(couponId), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  deletePromoCode(couponId: string) {
    return this.get(API_ENDPOINTS.PROMO_CODES.DELETE(couponId), {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  getReferralSettings() {
    return this.get<GetReferralSettingsApiEnvelope>(
      API_ENDPOINTS.BOOKINGS.GET_REFERRAL_SETTINGS,
      { baseURL: API_CONFIG.BASE_URL },
    );
  }

  saveReferralSettings(body: SaveReferralSettingsRequest) {
    return this.post<SaveReferralSettingsApiEnvelope>(
      API_ENDPOINTS.BOOKINGS.SAVE_REFERRAL_SETTINGS,
      body,
      { baseURL: API_CONFIG.BASE_URL },
    );
  }

  getRefunds(body: GetRefundsRequest) {
    return this.post(API_ENDPOINTS.REFUNDS.LIST, body, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  addRefund(
    data:
      | {
          bookingmasterid: number;
          bookingplanid: number;
          amount: number;
          refunddescription: string;
          refundType: string;

          isaddedbyweb: boolean;
          /** Edit-booking downgrade refund context from GetCostBreakdownDifference */
          planmessage?: string;
          planrefundableamountmessage?: string;
          totalplanprice?: number;
          totalrefundableamount?: number;
        }
      | Array<{
          bookingmasterid: number;
          bookingplanid: number;
          amount: number;
          refunddescription: string;
          refundType: string;

          isaddedbyweb: boolean;
          planmessage?: string;
          planrefundableamountmessage?: string;
          totalplanprice?: number;
          totalrefundableamount?: number;
        }>,
  ) {
    return this.post(API_ENDPOINTS.REFUNDS.ADD_REFUND, data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  initiateRefundProcess(bookingMasterId: string | number) {
    return this.post(
      API_ENDPOINTS.REFUNDS.INITIATE_REFUND_PROCESS(bookingMasterId),
      {},
      { baseURL: API_CONFIG.BASE_URL },
    );
  }

  initiateRefundProcessNew(data: {
    refundid: number;
    refundType: string;
    refundedamount: number;
    remark: string;
  }) {
    return this.post(
      "/Booking_Trip_ManagementService/Booking/InitiateRefundProcess",
      data,
      { baseURL: API_CONFIG.BASE_URL },
    );
  }

  /** PATCH `Booking/DeleteRefund/{refundid}` */
  deleteRefund(data: {
    refundid: string | number;
    deletereason: string;
    deleterefundreasonid: number;
  }) {
    return this.post(API_ENDPOINTS.REFUNDS.DELETE_REFUND(), data, {
      baseURL: API_CONFIG.BASE_URL,
    });
  }

  updateRefund(data: any) {
    return this.post(
      "/Booking_Trip_ManagementService/Booking/UpdateRefund",
      data,
      { baseURL: API_CONFIG.BASE_URL },
    );
  }
}

export default new ApiService();
